/* =====================================================================
   Portfolio analytics + the No-Regret optimizer.
   ---------------------------------------------------------------------
   The No-Regret Strategy is a real optimization (ARCHITECTURE §6.2):

     inputs      the focused (unhidden) scenarios weighted by their live
                 probabilities; the entity profile; existing strategies
                 as warm starts / keep-constraints ("already underway");
                 the candidate pool (library + the entity's custom
                 interventions + externally proposed candidates)

     objective   probability-weighted mean of the horizon-mean success
                 score across focused scenarios

     constraint  survive every focused world — lexicographic: fewer
                 deaths always beats higher score

     search      greedy forward selection with one-step lookahead,
                 followed by swap passes; deterministic throughout

   The full search trace is returned — the UI's "reasoning steps" are a
   rendering of this trace, not an animation.
   ===================================================================== */
import {
  type Entity, type Strategy, type InterventionProfile, type Priority,
  instantiatePreset, IV_PRESETS,
} from '@flourish/schema';
import { simulate } from './success.js';
import { HORIZONS } from './drivers.js';
import { SCENARIOS, type ScenarioId } from './scenarios.js';

/* ---------- scenario weighting ---------- */
export interface ScenarioWeight {
  id: ScenarioId;
  prob: number;              // 0..100 (0 = not modeled)
  focus?: boolean;
  hidden?: boolean;
}

/** Default weights: every modeled scenario, focused, at its 2026 probability. */
export function defaultWeights(): ScenarioWeight[] {
  return SCENARIOS.map(s => ({ id: s.id, prob: s.prob, focus: s.prob > 0, hidden: false }));
}

/** The scenario set the optimizer works against. */
export function focusedScenarios(weights: ScenarioWeight[]): ScenarioWeight[] {
  const visible = weights.filter(w => w.prob > 0 && !w.hidden);
  const focused = visible.filter(w => w.focus);
  return focused.length ? focused : visible;
}

/* ---------- portfolio evaluation ---------- */
function horizonMean(score: number[]): number {
  let s = 0;
  for (const h of HORIZONS) s += score[h];
  return s / HORIZONS.length;
}

export interface PortfolioEval {
  value: number;                       // probability-weighted horizon-mean score
  deaths: number;                      // focused scenarios in which the entity dies
  perScenario: { id: ScenarioId; utility: number; deathYear: number | null }[];
}

export function evaluatePortfolio(
  entity: Entity,
  interventions: InterventionProfile[],
  scenarios: ScenarioWeight[],
): PortfolioEval {
  const st: Strategy = { id: '_eval', name: '_eval', desc: '', isNoRegret: false, isMostLikely: false, interventions };
  let vs = 0, ws = 0, deaths = 0;
  const perScenario: PortfolioEval['perScenario'] = [];
  for (const w of scenarios) {
    const sim = simulate(entity, st, w.id);
    const u = horizonMean(sim.score);
    perScenario.push({ id: w.id, utility: u, deathYear: sim.deathYear });
    vs += w.prob * u;
    ws += w.prob;
    if (sim.deathYear !== null) deaths += 1;
  }
  return { value: ws ? vs / ws : 0, deaths, perScenario };
}

/** Probability-weighted end-horizon score over the visible scenarios —
    the single number shown on entity cards. */
export function overallSuccess(entity: Entity, strategy: Strategy, weights: ScenarioWeight[]): number {
  let a = 0, w = 0;
  for (const sw of weights) {
    if (sw.prob <= 0 || sw.hidden) continue;
    const sim = simulate(entity, strategy, sw.id);
    a += sw.prob * sim.score[50];
    w += sw.prob;
  }
  return w ? Math.round(a / w) : 0;
}

/* ---------- the optimizer ---------- */
export interface OptimizerStep {
  kind: 'seed' | 'add' | 'swap' | 'assign-priorities' | 'stop';
  detail: string;
  evaluated?: number;
  chosen?: string;
  gain?: number;
  deaths?: number;
  value?: number;
  drivingScenario?: ScenarioId;
}

export interface OptimizerResult {
  interventions: InterventionProfile[];
  eval: PortfolioEval;
  trace: OptimizerStep[];
  evaluations: number;
}

export interface OptimizeOptions {
  scenarios?: ScenarioWeight[];              // default: defaultWeights()
  candidates?: InterventionProfile[];        // default: the 14 library presets
  keep?: InterventionProfile[];              // "already underway" — always included
  maxSize?: number;                          // default 8
  minGain?: number;                          // stop threshold on value gain, default 0.25
}

/** better = fewer deaths, then higher value (lexicographic) */
function better(a: { deaths: number; value: number }, b: { deaths: number; value: number }): boolean {
  return a.deaths < b.deaths || (a.deaths === b.deaths && a.value > b.value + 1e-9);
}

export function defaultCandidates(): InterventionProfile[] {
  return Object.keys(IV_PRESETS).map(ref =>
    instantiatePreset(ref, { iid: `nr_${ref}`, priority: 'Medium' }));
}

export function optimizeNoRegret(entity: Entity, opts: OptimizeOptions = {}): OptimizerResult {
  const weights = focusedScenarios(opts.scenarios ?? defaultWeights());
  const pool = (opts.candidates ?? defaultCandidates()).slice();
  const keep = opts.keep ?? [];
  const maxSize = opts.maxSize ?? 8;
  const minGain = opts.minGain ?? 0.25;

  const trace: OptimizerStep[] = [];
  let evaluations = 0;
  const evalP = (ivs: InterventionProfile[]): PortfolioEval => {
    evaluations += 1;
    return evaluatePortfolio(entity, ivs, weights);
  };

  // deterministic candidate order
  pool.sort((a, b) => a.name.localeCompare(b.name));

  let portfolio: InterventionProfile[] = [...keep];
  let current = evalP(portfolio);
  trace.push({
    kind: 'seed',
    detail: keep.length
      ? `starting from ${keep.length} intervention(s) already underway`
      : 'starting from a blank portfolio',
    value: round1(current.value), deaths: current.deaths,
  });

  const inPortfolio = new Set(portfolio.map(iv => keyOf(iv)));

  // --- greedy forward selection
  while (portfolio.length < maxSize) {
    let best: { iv: InterventionProfile; ev: PortfolioEval } | null = null;
    let evaluated = 0;
    for (const cand of pool) {
      if (inPortfolio.has(keyOf(cand))) continue;
      const ev = evalP([...portfolio, cand]);
      evaluated += 1;
      if (!best || better(ev, best.ev)) best = { iv: cand, ev };
    }
    if (!best) break;
    const gained = better(best.ev, current);
    const gain = best.ev.value - current.value;
    if (!gained || (best.ev.deaths === current.deaths && gain < minGain)) {
      trace.push({
        kind: 'stop',
        detail: `no candidate improves the portfolio enough (best gain ${round1(gain)} pts)`,
        evaluated,
      });
      break;
    }
    portfolio = [...portfolio, best.iv];
    inPortfolio.add(keyOf(best.iv));
    trace.push({
      kind: 'add',
      detail: describeGain(best.iv.name, current, best.ev, weights, entity, portfolio),
      chosen: best.iv.name,
      evaluated,
      gain: round1(gain),
      value: round1(best.ev.value),
      deaths: best.ev.deaths,
      drivingScenario: drivingScenario(current, best.ev),
    });
    current = best.ev;
  }

  // --- swap passes (replace a non-kept member with an unused candidate)
  const keptKeys = new Set(keep.map(iv => keyOf(iv)));
  for (let pass = 0; pass < 3; pass++) {
    let improved = false;
    for (let i = 0; i < portfolio.length; i++) {
      if (keptKeys.has(keyOf(portfolio[i]))) continue;
      for (const cand of pool) {
        if (inPortfolio.has(keyOf(cand))) continue;
        const swapped = portfolio.slice();
        swapped[i] = cand;
        const ev = evalP(swapped);
        if (better(ev, current)) {
          trace.push({
            kind: 'swap',
            detail: `${portfolio[i].name} -> ${cand.name} (+${round1(ev.value - current.value)} pts)`,
            chosen: cand.name,
            value: round1(ev.value),
            deaths: ev.deaths,
          });
          inPortfolio.delete(keyOf(portfolio[i]));
          inPortfolio.add(keyOf(cand));
          portfolio = swapped;
          current = ev;
          improved = true;
          break;
        }
      }
    }
    if (!improved) break;
  }

  // --- assign priorities by leave-one-out contribution
  const withPriorities = assignPriorities(entity, portfolio, weights, keptKeys, () => { evaluations += 1; });
  trace.push({
    kind: 'assign-priorities',
    detail: 'priorities assigned by each intervention\u2019s contribution across the focused worlds',
  });

  const finalEval = evalP(withPriorities);
  trace.push({
    kind: 'stop',
    detail: `final portfolio: ${withPriorities.length} interventions · value ${round1(finalEval.value)} · ` +
            `${finalEval.deaths === 0 ? 'survives every focused world' : `dies in ${finalEval.deaths} focused world(s)`}`,
    value: round1(finalEval.value),
    deaths: finalEval.deaths,
  });

  return { interventions: withPriorities, eval: finalEval, trace, evaluations };
}

function keyOf(iv: InterventionProfile): string {
  return iv.ref ?? `custom:${iv.name.toLowerCase()}`;
}
function round1(v: number): number { return Math.round(v * 10) / 10; }

function drivingScenario(before: PortfolioEval, after: PortfolioEval): ScenarioId | undefined {
  let best: ScenarioId | undefined, bestGain = -Infinity;
  for (const a of after.perScenario) {
    const b = before.perScenario.find(x => x.id === a.id);
    if (!b) continue;
    const g = a.utility - b.utility;
    if (g > bestGain) { bestGain = g; best = a.id; }
  }
  return best;
}

function describeGain(
  name: string, before: PortfolioEval, after: PortfolioEval,
  weights: ScenarioWeight[], entity: Entity, portfolio: InterventionProfile[],
): string {
  const savedWorlds = after.perScenario.filter(a =>
    a.deathYear === null && before.perScenario.find(b => b.id === a.id)?.deathYear !== null);
  if (savedWorlds.length) return `${name} — prevents collapse in ${savedWorlds.map(s => s.id).join(', ')}`;
  const drv = drivingScenario(before, after);
  return drv ? `${name} — biggest lift in ${drv}` : name;
}

function assignPriorities(
  entity: Entity,
  portfolio: InterventionProfile[],
  weights: ScenarioWeight[],
  keptKeys: Set<string>,
  onEval: () => void,
): InterventionProfile[] {
  if (!portfolio.length) return [];
  const full = evaluatePortfolio(entity, portfolio, weights); onEval();
  const scored = portfolio.map(iv => {
    const without = portfolio.filter(x => x.iid !== iv.iid);
    const ev = evaluatePortfolio(entity, without, weights); onEval();
    const marginal = (full.value - ev.value) + (ev.deaths - full.deaths) * 100; // saving a world dominates
    return { iv, marginal };
  });
  scored.sort((a, b) => b.marginal - a.marginal || a.iv.name.localeCompare(b.iv.name));
  const n = scored.length;
  return scored.map(({ iv }, rank) => {
    const priority: Priority = keptKeys.has(keyOf(iv)) ? iv.priority
      : rank < Math.ceil(n / 3) ? 'High'
      : rank < Math.ceil((2 * n) / 3) ? 'Medium' : 'Low';
    return { ...iv, priority };
  });
}

/* ---------- per-intervention stats for the UI (pos/neg + pareto) ---------- */
export interface InterventionStat { iid: string; name: string; net: number; }

export function interventionStats(
  entity: Entity, strategy: Strategy, weights: ScenarioWeight[],
): { count: number; pos: number; neg: number; detail: InterventionStat[] } {
  const scenarios = focusedScenarios(weights);
  const full = evaluatePortfolio(entity, strategy.interventions, scenarios);
  const detail: InterventionStat[] = strategy.interventions.map(iv => {
    const without = strategy.interventions.filter(x => x.iid !== iv.iid);
    const ev = evaluatePortfolio(entity, without, scenarios);
    return { iid: iv.iid, name: iv.name, net: Math.round((full.value - ev.value) * 10) / 10 };
  });
  return {
    count: detail.length,
    pos: detail.filter(d => d.net >= 0).length,
    neg: detail.filter(d => d.net < 0).length,
    detail,
  };
}

/** Which interventions carry ~80% of the (absolute) impact. */
export function pareto(entity: Entity, strategy: Strategy, weights: ScenarioWeight[]) {
  const d = interventionStats(entity, strategy, weights).detail
    .map(x => ({ ...x, abs: Math.abs(x.net) }))
    .sort((a, b) => b.abs - a.abs || a.name.localeCompare(b.name));
  const total = d.reduce((s, x) => s + x.abs, 0) || 1;
  let cum = 0;
  const top: typeof d = [];
  for (const x of d) {
    top.push(x); cum += x.abs;
    if (cum / total >= 0.8) break;
  }
  return { top, share: Math.round((cum / total) * 100) };
}
